import React, { useState, useEffect } from 'react';
import { 
  FiDollarSign, FiSearch, FiCheckCircle, FiClock, FiTrash2, 
  FiSettings, FiSave, FiInfo, FiRefreshCw 
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function CommissionsPage() {
  const [commissions, setCommissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  // States สำหรับตั้งค่าคอมมิชชันเริ่มต้นของระบบ
  const [commSettings, setCommSettings] = useState({
    commission_type: 'fixed',
    commission_value: '200'
  });

  // โหลดข้อมูลคอมมิชชันทั้งหมด และการตั้งค่ากลางของระบบ
  const loadData = async () => {
    setIsLoading(true);
    try {
      // 1. ดึงรายการค่าคอมมิชชัน
      const resComm = await fetch(`${API_URL}?action=get_commissions`);
      const dataComm = await resComm.json();
      if (dataComm.success) {
        setCommissions(dataComm.data || []);
      }

      // 2. ดึงการตั้งค่ากลางจาก website_settings
      const resSet = await fetch(`${API_URL}?action=get_website_settings`);
      const dataSet = await resSet.json();
      if (dataSet.success && dataSet.data) {
        setCommSettings({
          commission_type: dataSet.data.commission_type || 'fixed',
          commission_value: dataSet.data.commission_value || '200'
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire('ข้อผิดพลาด', 'ดึงข้อมูลไม่สำเร็จ', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // บันทึกการตั้งค่าระบบหลัก (Global Settings)
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    Swal.fire({ title: 'กำลังบันทึกตั้งค่าระบบ...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const res = await fetch(`${API_URL}?action=update_website_settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(commSettings)
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire('สำเร็จ!', 'บันทึกตั้งค่าอัตราคอมมิชชันเรียบร้อยแล้ว', 'success');
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('ข้อผิดพลาด', err.message || 'ไม่สามารถตั้งค่าได้', 'error');
    }
  };

  // อัปเดตรายแถว (กำหนดจำนวนเงิน หรือ เปลี่ยนสถานะ)
  const handleUpdateCommission = async (item, newAmount, newStatus, notes = '') => {
    try {
      const res = await fetch(`${API_URL}?action=update_commission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.id,
          commission_amount: newAmount,
          status: newStatus,
          admin_notes: notes || item.admin_notes || ''
        })
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire({
          title: 'สำเร็จ!',
          text: 'ปรับปรุงค่าคอมมิชชันและสถานะเรียบร้อย',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500
        });
        loadData();
      } else {
        alert('เกิดข้อผิดพลาด: ' + data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // ลบประวัติค่าคอมมิชชัน
  const handleDeleteCommission = (id) => {
    Swal.fire({
      title: 'ต้องการลบประวัตินี้?',
      text: "คุณไม่สามารถย้อนคืนประวัติการจ่ายค่าคอมมิชชันนี้ได้!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ใช่, ต้องการลบ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API_URL}?action=delete_commission`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id })
          });
          const data = await res.json();
          if (data.success) {
            Swal.fire('ลบข้อมูลแล้ว!', 'ประวัติค่าคอมมิชชันถูกลบออกจากตารางเรียบร้อย', 'success');
            loadData();
          } else {
            Swal.fire('ผิดพลาด', data.message, 'error');
          }
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  // นำรายการค่าคอมมาคำนวณสรุปการเงิน
  const totalPending = commissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + Number(c.commission_amount), 0);
  const totalApproved = commissions.filter(c => c.status === 'approved').reduce((sum, c) => sum + Number(c.commission_amount), 0);
  const totalPaid = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + Number(c.commission_amount), 0);

  // กรองรายการค่าคอมตามแถบค้นหาและสถานะที่เลือก
  const filteredCommissions = commissions.filter(c => {
    const matchesSearch = 
      c.tech_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.tech_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    return matchesSearch && c.status === statusFilter;
  });

  return (
    <div className="space-y-6 pb-12 text-left text-gray-800 dark:text-gray-100 font-sans">
      
      {/* ส่วนหัวหน้าจอ */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-850 dark:text-white flex items-center gap-2">
            <span className="text-blue-500"><FiDollarSign /></span> จัดการค่าคอมมิชชันช่าง (Referral Commissions)
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            อนุมัติและโอนจ่ายค่านายหน้าจากการแนะนำลูกค้าใหม่ของช่างแต่ละคน โดยแยกความเป็นอิสระออกจากการจ่ายเงินเดือนปกติ
          </p>
        </div>
        <button 
          onClick={loadData}
          className="p-2.5 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 text-gray-700 dark:text-gray-300 rounded-xl transition border-none cursor-pointer flex items-center gap-1.5 font-bold text-xs"
        >
          <FiRefreshCw size={14}/> รีเฟรชข้อมูล
        </button>
      </div>

      {/* สถิติสรุปยอดเงินค่าคอม */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm text-left relative overflow-hidden">
          <div className="absolute top-0 right-0 w-2 h-full bg-amber-500"></div>
          <span className="text-[10px] font-black text-gray-450 uppercase tracking-widest block">⏳ รออนุมัติ (Pending)</span>
          <span className="text-2xl font-black text-amber-600 dark:text-amber-400 block mt-2">{totalPending.toLocaleString()} ฿</span>
          <span className="text-[9px] text-gray-400 block mt-1">จำนวนที่รอยืนยันอัตราเงินปันผลรายเคส</span>
        </div>

        <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm text-left relative overflow-hidden">
          <div className="absolute top-0 right-0 w-2 h-full bg-blue-500"></div>
          <span className="text-[10px] font-black text-gray-450 uppercase tracking-widest block">✅ อนุมัติแล้ว/รอจ่าย (Approved)</span>
          <span className="text-2xl font-black text-blue-600 dark:text-blue-400 block mt-2">{totalApproved.toLocaleString()} ฿</span>
          <span className="text-[9px] text-gray-400 block mt-1">ยอดผ่านการอนุมัติแล้ว รอรอบกดทำธุรกรรมโอนเงิน</span>
        </div>

        <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm text-left relative overflow-hidden">
          <div className="absolute top-0 right-0 w-2 h-full bg-emerald-500"></div>
          <span className="text-[10px] font-black text-gray-450 uppercase tracking-widest block">💰 โอนเงินแล้ว (Paid)</span>
          <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400 block mt-2">{totalPaid.toLocaleString()} ฿</span>
          <span className="text-[9px] text-gray-400 block mt-1">รายการที่ยืนยันการโอนเงินด่วนเข้าบัญชีช่างแล้ว</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* แผงตารางจัดการค่าคอม (2 ส่วน) */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl shadow-sm overflow-hidden">
            
            {/* แถบตัวกรอง */}
            <div className="p-5 border-b border-gray-200 dark:border-[#1e2330] bg-gray-50/50 dark:bg-[#1b2132]/30 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="relative w-full md:w-72">
                <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="ค้นหาชื่อช่าง, ไอดี, หรือชื่อลูกค้า..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs text-gray-800 dark:text-white outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex gap-2 w-full md:w-auto">
                <select 
                  value={statusFilter} 
                  onChange={e => setStatusFilter(e.target.value)}
                  className="p-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-xs text-gray-800 dark:text-white outline-none cursor-pointer font-bold"
                >
                  <option value="all">ทั้งหมด (ทุกสถานะ)</option>
                  <option value="pending">⏳ รออนุมัติ</option>
                  <option value="approved">✅ อนุมัติแล้ว / รอโอน</option>
                  <option value="paid">💰 โอนเงินแล้ว</option>
                </select>
              </div>
            </div>

            {/* ตารางข้อมูล */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-gray-700 dark:text-gray-300">
                <thead className="text-[10px] uppercase bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-[#334155]">
                  <tr>
                    <th className="px-4 py-4">ข้อมูลช่างแนะนำ</th>
                    <th className="px-4 py-4">ข้อมูลลูกค้าใหม่</th>
                    <th className="px-4 py-4 text-right">ยอดงานจริง</th>
                    <th className="px-4 py-4 text-center">ค่าคอมมิชชัน (฿)</th>
                    <th className="px-4 py-4 text-center">สถานะธุรกรรม</th>
                    <th className="px-4 py-4 text-right">การเบิกจ่าย</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-150 dark:divide-gray-800">
                  {isLoading ? (
                    <tr><td colSpan="6" className="text-center py-10">กำลังโหลดรายการค่าคอมมิชชัน...</td></tr>
                  ) : filteredCommissions.length > 0 ? filteredCommissions.map((item, idx) => {
                    return (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-[#1e2330]/30 transition-colors">
                        {/* ช่างผู้แนะนำ */}
                        <td className="px-4 py-4">
                          <p className="font-extrabold text-gray-850 dark:text-white text-sm">{item.tech_name}</p>
                          <p className="text-[10px] text-gray-400 font-mono">ID: {item.tech_username}</p>
                        </td>

                        {/* ลูกค้า */}
                        <td className="px-4 py-4">
                          <p className="font-bold text-gray-800 dark:text-white">{item.customer_name || 'ลูกค้าทั่วไป'}</p>
                          <p className="text-[9px] text-gray-400">ใบงาน ID: {item.job_id || '-'}</p>
                        </td>

                        {/* ยอดปิดงาน */}
                        <td className="px-4 py-4 text-right font-bold">
                          {Number(item.service_amount).toLocaleString()} ฿
                        </td>

                        {/* ยอดเงินปันผล (แก้ค่าเงินได้อิสระ) */}
                        <td className="px-4 py-4 text-center">
                          {item.status === 'paid' ? (
                            <span className="font-black text-emerald-600 dark:text-emerald-400 text-sm">
                              {Number(item.commission_amount).toLocaleString()} ฿
                            </span>
                          ) : (
                            <input 
                              type="number"
                              defaultValue={item.commission_amount}
                              onBlur={(e) => {
                                const newAmount = parseFloat(e.target.value) || 0;
                                if (newAmount !== Number(item.commission_amount)) {
                                  handleUpdateCommission(item, newAmount, item.status);
                                }
                              }}
                              placeholder="จำนวนเงิน"
                              className="w-20 p-1.5 text-center font-black bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-xs text-blue-600 outline-none"
                            />
                          )}
                        </td>

                        {/* สถานะ */}
                        <td className="px-4 py-4 text-center">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black border
                            ${item.status === 'paid' 
                              ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' 
                              : item.status === 'approved'
                              ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                              : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                            }
                          `}>
                            {item.status === 'paid' ? 'โอนแล้ว' : item.status === 'approved' ? 'อนุมัติแล้ว' : 'รออนุมัติ'}
                          </span>
                        </td>

                        {/* ปุ่มควบคุม */}
                        <td className="px-4 py-4 text-right">
                          <div className="flex justify-end gap-1.5">
                            {item.status === 'pending' && (
                              <button
                                onClick={() => handleUpdateCommission(item, item.commission_amount, 'approved')}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-2 py-1.5 rounded text-[10px] cursor-pointer border-none"
                              >
                                ✔ อนุมัติ
                              </button>
                            )}

                            {item.status === 'approved' && (
                              <button
                                onClick={() => {
                                  const notes = prompt('ใส่รหัสธุรกรรม/ลิงก์รูปสลิปการโอนเงิน (ถ้ามี):');
                                  handleUpdateCommission(item, item.commission_amount, 'paid', notes || '');
                                }}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold px-2 py-1.5 rounded text-[10px] cursor-pointer border-none"
                              >
                                💰 ยืนยันโอนเงิน
                              </button>
                            )}

                            <button
                              onClick={() => handleDeleteCommission(item.id)}
                              className="p-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/30 text-red-650 rounded cursor-pointer border-none"
                              title="ลบรายการ"
                            >
                              <FiTrash2 size={12}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="text-center py-10 text-gray-500">ไม่มีประวัติการรับค่าคอมมิชชัน</td></tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        {/* แผงด้านขวา: ระบบตั้งค่ากลาง (Global Settings Policy) */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-[#131825] border border-gray-200 dark:border-[#1e2330] rounded-3xl p-5 shadow-sm">
            <h2 className="text-sm font-black text-gray-850 dark:text-white flex items-center gap-1.5 mb-2">
              <span className="text-indigo-500"><FiSettings /></span> ตั้งค่านโยบายกลางคอมมิชชัน
            </h2>
            <p className="text-[10px] text-gray-400 leading-relaxed mb-4">
              กำหนดอัตราส่วนแบ่งค่านายหน้าแนะนำลูกค้าใหม่เริ่มต้น เพื่อให้หลังบ้านนำไปคํานวณเงินให้อัตโนมัติเมื่อเกิดการบันทึกรายการแนะนำครั้งแรก
            </p>

            <form onSubmit={handleSaveSettings} className="space-y-4 text-xs text-left">
              <div>
                <label className="block font-bold text-gray-600 dark:text-gray-400 mb-1.5">รูปแบบค่านายหน้าเริ่มต้น</label>
                <select
                  value={commSettings.commission_type}
                  onChange={e => setCommSettings({ ...commSettings, commission_type: e.target.value })}
                  className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] text-gray-850 dark:text-white border border-gray-300 dark:border-[#334155] rounded-xl font-bold outline-none cursor-pointer"
                >
                  <option value="fixed">ยอดเงินคงที่ (บาทต่อรอบบริการ)</option>
                  <option value="percent">คิดตามเปอร์เซ็นต์ (% จากยอดปิดงาน)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-600 dark:text-gray-400 mb-1.5">
                  {commSettings.commission_type === 'fixed' ? 'จำนวนเงินปันผลคงที่ (บาท)' : 'ส่วนแบ่งเปอร์เซ็นต์ (%)'}
                </label>
                <input 
                  type="number"
                  required
                  value={commSettings.commission_value}
                  onChange={e => setCommSettings({ ...commSettings, commission_value: e.target.value })}
                  placeholder={commSettings.commission_type === 'fixed' ? 'เช่น 200' : 'เช่น 5'}
                  className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] text-gray-850 dark:text-white border border-gray-300 dark:border-[#334155] rounded-xl font-bold outline-none focus:border-blue-500"
                />
              </div>

              <button 
                type="submit"
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs rounded-xl shadow transition cursor-pointer border-none flex justify-center items-center gap-1.5"
              >
                <FiSave size={14}/> บันทึกนโยบายเริ่มต้น
              </button>
            </form>
          </div>

          {/* กล่องให้คำแนะนำ */}
          <div className="bg-indigo-50 dark:bg-[#1e2330]/35 border border-indigo-150 dark:border-[#2d1b69]/40 rounded-3xl p-5 text-xs text-left">
            <h3 className="font-extrabold text-indigo-700 dark:text-[#c4b5fd] flex items-center gap-1.5 mb-2">
              <FiInfo size={14}/> ข้อมูลระบบจ่ายเงินคอมมิชชัน
            </h3>
            <ul className="list-disc list-inside space-y-1.5 text-gray-600 dark:text-gray-300 font-medium text-[10px] leading-relaxed">
              <li>ระบบนี้ถูก<b>แยกขาดจากระบบเงินเดือนหลัก</b>เพื่อความรวดเร็วในการโอนจ่าย</li>
              <li>แอดมินสามารถป้อนพิมพ์เปลี่ยนจำนวนเงินในแต่ละแถวได้ทันที จากนั้นระบบจะบันทึกอัปเดตให้อัตโนมัติ</li>
              <li>ช่างผู้แนะนำจะตรวจเช็คยอดเงินของตนเอง และตรวจสอบสถานะโอนเงินแบบเรียลไทม์ได้จากแอปของฝั่งช่าง</li>
            </ul>
          </div>
        </div>

      </div>

    </div>
  );
}
